import { CalendarToday, ChevronRight } from "@mui/icons-material";
import React from "react";
import CountUp from "react-countup";
import { BsCalendar } from "react-icons/bs";
import { FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import OverflowString from "./OverflowString";

// Type definitions for Button component
interface ButtonProps {
  children: React.ReactNode;
  variant?: 'card' | 'outline' | 'solid' | 'gradient' | 'ghost' | 'success' | 'warning' | 'danger' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  showArrow?: boolean;
  className?: string;
}

// Modular Button Component
const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'card',
  size = 'md',
  onClick = () => { },
  disabled = false,
  icon,
  showArrow = true,
  className = ''
}) => {
  const variants = {
    card: 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 hover:border-blue-300',
    outline: 'bg-white border-2 border-blue-500 text-blue-600 hover:bg-blue-500 hover:text-white',
    solid: 'bg-blue-500 text-white hover:bg-blue-600',
    gradient: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white',
    ghost: 'bg-transparent text-blue-600 hover:bg-blue-50',
    success: 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 hover:border-green-300',
    warning: 'bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border border-yellow-200 hover:border-yellow-300',
    danger: 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 hover:border-red-300',
    dark: 'bg-gray-800 text-white hover:bg-gray-900'
  };

  const sizes = {
    sm: 'py-2 px-3 text-sm',
    md: 'py-3 px-4 text-sm',
    lg: 'py-4 px-6 text-base'
  };

  const ArrowIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );

  const CalendarIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
    </svg>
  );

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full font-medium rounded-xl transition-all duration-200 hover:shadow-md
        ${variants[variant]} ${sizes[size]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        flex items-center justify-between
        ${className}
      `}
    >
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
          {icon || <CalendarIcon />}
        </div>
        <div className="text-left">
          <div className="font-semibold">{children}</div>
          <div className="text-xs opacity-75">Manage all bookings</div>
        </div>
      </div>
      {showArrow && <ArrowIcon />}
    </button>
  );
};


const DashboardCard2 = ({
  value,
  name,
  availableDocCount,
  role,
  hospitalID,
  doctorId,
}: {
  value: string;
  name: string;
  availableDocCount: string;
  role?: string;
  hospitalID?: string;
  doctorId?: string | undefined;
}) => {
  const { doctorsData } = useHospDocData();
  const navigate = useNavigate();

  // Get doctor data safely
  const doctorData = doctorsData?.find((e) => e.doctor_id === doctorId);

  return (
    <div
      className={`flex flex-col ${doctorId ? "items-start" : "items-center"} justify-between w-full  bg-white rounded-lg px-4 py-5 lg:px-5 lg:py-6`}
       data-aos="zoom-in"
      data-aos-duration="1000"
    >
      {role === "Doctor" ? (
        <>
          <div className="flex items-center justify-start mb-3">
            {/* Avatar */}
            <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg flex-shrink-0">
              {doctorData?.full_name ? doctorData.full_name.substring(0, 2).toUpperCase() : "DR"}
            </div>

            {/* Doctor Info */}
            <div className="flex-1 min-w-0 ml-3">
              <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1 truncate">
              <OverflowString text= {`Dr. ${doctorData?.full_name || "Doctor Name"}`} className="!text-lg !text-black !font-bold" length={15} />
               
              </h3>
              <p className="text-blue-600 text-sm font-medium mb-1 truncate">
              <OverflowString text= {doctorData?.qualification || "Qualification"} className="!text-blue-600 !text-sm !font-medium" length={15} />
                
              </p>
              <p className="text-gray-600 text-sm truncate">
                <OverflowString text={doctorData?.specialization || "Specialization"} className="!text-sm" length={20} />
              </p>
            </div>
          </div>
          {/* Action Button */}
          <Button
            variant="card"
            size="md"
            onClick={() => navigate("/" + hospitalID + "/doctors/doc/" + doctorData?.mapping_id)}
            icon={<FaCalendarAlt className="w-4 h-4" />}
          >
            View Appointments
          </Button>
        </>

      ) : (
        <>
          <div className="text-center h-20">
            <p className="text-4xl lg:text-4xl text-dark tracking-[0.4px] font-bold">
              <CountUp
                duration={2}
                className="counter"
                end={Number(availableDocCount)}
              />
              <span className="text-2xl text-dbGray">/{value}</span>
            </p>
            <p className="text-sm lg:text-sm text-dbGray tracking-[0.2px] mt-2 font-medium">
              {name}
            </p>
          </div>
          <div
            className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 w-full hover:from-blue-100 hover:to-indigo-100 transition-all duration-300 cursor-pointer group"
            onClick={() =>
              navigate("/" + hospitalID + "/appointments/book-an-appointment")
            }
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center group-hover:bg-blue-700 transition-colors">
                  <FaCalendarAlt className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">
                    Schedule New Appointment
                  </h4>
                  <p className="text-xs text-gray-500">
                    Book with available doctors
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            </div>
          </div>
        </>
      )}

      {/* <p className="text-lg mt-4 md:mt-8 relative top-3 text-dbGray tracking-[0.4px] font-bold h-14 text-center">
        Available Doctors
      </p> */}
    </div>
  );
};

export default DashboardCard2;
